import React, { useEffect } from 'react';
import PropTypes from 'prop-types';
import { get } from 'lodash';
import { Link } from 'react-router-dom';
import dayjs from 'dayjs';
import { isMobile } from 'react-device-detect';
import * as Styled from './styles';
import { useEntry } from '../../hooks/useEntry';
import { useAuth } from '../../hooks/useAuth';
import { useSweep } from '../../hooks/useSweep';
import Winners from '../../components/Winners';
import { RunnerCard } from '../../components/RunnerCard';
import { Layout } from '../../components/Layout';
import CountDownTimer from '../../components/CountdownTimer';
import Entered from '../../components/Entered';
import { UpcomingRounds } from '../../components/UpcomingRounds';
import { checkIfExcludedFrom } from '../../utils/authUtils';
import { BetPromo } from '../../components/BetPromo';
import { useFeatures } from '../../hooks/useFeatures';
import { getBettingStatus } from '../../helpers/betHelpers';
import { getPromotionConfig } from '../../components/BetPromo/utils';
import RevealSelectionAnimation from '../../components/RevealSelectionAnimation';
import PlayItv from '../../components/CallToActions/PlayItv';
import ShareDesktop from '../../components/ShareDesktop';
import ShareMobile from '../../components/ShareMobile';
import GoogleAnalyticsMetaDataObj from '../../helpers/googleAnalyticsHelpers/buildGoogleAnalyticsMetaDataObject';
import { sendMetadataEvent } from '../../helpers/googleAnalyticsHelpers/gaHelper';
import { didEntryMatchWinner } from './utils';

const TermsLinkWrapper = ({ children }) => {
    return <Link to="/terms">{children}</Link>;
};

const Itv7TermsLinkWrapper = ({ children }) => {
    return (
        <Styled.ButtonLink href="https://itv7.itv.com/terms" target="_blank">
            {children}
        </Styled.ButtonLink>
    );
};

const ButtonLinkWrapper = ({ children }) => {
    return (
        <Styled.ButtonLink href="https://itv7.itv.com/home">
            {children}
        </Styled.ButtonLink>
    );
};

export const EnteredContainer = ({ OnLoggedOut }) => {
    const { sweep, setSweep } = useSweep();
    const { entry, setEntry } = useEntry();
    const { authStore } = useAuth();
    const { features } = useFeatures();

    const googleAnalyticsMetadata = new GoogleAnalyticsMetaDataObj(
        authStore.user.uki_customerid,
        authStore.loggedIn,
        authStore.user['com.sbgcore.upgradable']
    );

    useEffect(() => {
        sendMetadataEvent(googleAnalyticsMetadata, 'Sweepstake');
    }, []);

    useEffect(() => {
        if (!authStore.loggedIn && sweep && sweep.sweepId) {
            setSweep({
                prize: '-',
                name: '-',
                date: '-',
                sweepId: null,
                event: {}
            });

            setEntry({
                id: null,
                name: null,
                metaData: {},
                hasEntered: false
            });
        }
    }, [sweep, entry, authStore.loggedIn]);

    if (!authStore.loggedIn) {
        return OnLoggedOut;
    }

    const { event } = sweep;
    const { winningSelections = [] } = event;

    const startDate = get(sweep, 'event.startDate', null);
    const eventStartDateTime = dayjs(startDate);
    const now = dayjs();
    const hasEventStarted = now.isAfter(eventStartDateTime);

    const hasWinners = !!winningSelections.length;
    const excludeFromBetting = checkIfExcludedFrom('bet');
    const hasEntryWon = didEntryMatchWinner(winningSelections, entry.id);

    const hasSeenEntry = localStorage.getItem('has_seen_entry') !== null;
    const showEntryAnimation =
        get(features, 'entry_animation_enabled', false) &&
        !hasSeenEntry &&
        !!entry.name;

    const displayCountdownTimer =
        get(features, 'countdown_timer_enabled', false) &&
        !hasWinners &&
        !hasEventStarted;

    const promotionsConfig = getPromotionConfig({
        promotions: features.promotions,
        hasEventStarted
    });

    const displayBettingLinks = get(features, 'betting_links_enabled', false);
    const isLoggedIn = get(authStore, 'loggedIn', false);

    const bettingEnabled = getBettingStatus(
        isLoggedIn,
        displayBettingLinks,
        !excludeFromBetting
    );

    const shareEnabled = get(features, 'share_enabled', false);

    const isGoldCup = get(features, 'is_gold_cup', false);

    return (
        <EnteredPage
            showEntryAnimation={showEntryAnimation}
            loggedIn={authStore.loggedIn}
            userName={authStore.user.given_name}
            excludeFromBetting={excludeFromBetting}
            sweep={sweep}
            hasEventStarted={hasEventStarted}
            hasWinners={hasWinners}
            winningSelections={winningSelections}
            entry={entry}
            TermsLink={TermsLinkWrapper}
            Itv7TermsLink={Itv7TermsLinkWrapper}
            ButtonLink={ButtonLinkWrapper}
            didEntryWin={hasEntryWon}
            displayCountdownTimer={displayCountdownTimer}
            bettingEnabled={bettingEnabled}
            isGoldCup={isGoldCup}
            promotionsConfig={promotionsConfig}
            shareEnabled={shareEnabled}
        />
    );
};

export const EnteredPage = ({
    showEntryAnimation,
    loggedIn,
    userName,
    excludeFromBetting,
    sweep,
    hasEventStarted,
    hasWinners,
    winningSelections,
    entry,
    didEntryWin,
    TermsLink,
    Itv7TermsLink,
    ButtonLink,
    displayCountdownTimer,
    bettingEnabled,
    // isGoldCup,
    promotionsConfig,
    shareEnabled
}) => {
    const title = get(sweep, 'name', '-');
    const prize = get(sweep, 'prize', '-');
    const eventName = get(sweep, `event.name`, '');
    const startDate = get(sweep, 'event.startDate', null);

    return (
        <>
            {showEntryAnimation && (
                <RevealSelectionAnimation title={title} entry={entry} />
            )}

            <Layout loggedIn={loggedIn} userName={userName}>
                {displayCountdownTimer && (
                    <Styled.CountDownTimerWrapper>
                        {startDate && (
                            <CountDownTimer
                                name={eventName}
                                date={new Date(startDate).getTime()}
                            />
                        )}
                    </Styled.CountDownTimerWrapper>
                )}
                <Styled.ContentWrapper>
                    {hasWinners ? (
                        <Winners
                            title={title}
                            eventName={eventName}
                            winners={winningSelections}
                            didEntryWin={didEntryWin}
                            entry={entry}
                            TermsLink={TermsLink}
                            Itv7TermsLink={Itv7TermsLink}
                            prize={prize}
                            ButtonLink={ButtonLink}
                        />
                    ) : (
                        <Entered
                            title={title}
                            eventName={eventName}
                            prize={prize}
                            hasEventStarted={hasEventStarted}
                            entry={entry}
                            excludeFromBetting={excludeFromBetting}
                            startDate={startDate}
                            TermsLink={TermsLink}
                            bettingEnabled={bettingEnabled}
                        />
                    )}
                    {shareEnabled && !hasWinners && !hasEventStarted && (
                        <Styled.ShareWrapper>
                            {isMobile ? (
                                <ShareMobile
                                    copy={`I’ve just entered the ${title} Sweepstake 🏇 don’t miss your chance to enter for free and be in with a chance of winning ${prize}! #ITV7 itv7sweepstake.itv.com 18+ and UK only, T&Cs apply.`}
                                    title={`Join the race for ${prize}!`}
                                />
                            ) : (
                                <ShareDesktop
                                    copy={`I’ve just entered the ${title} Sweepstake 🏇 don’t miss your chance to enter for free and be in with a chance of winning ${prize}! #ITV7 itv7sweepstake.itv.com 18+ and UK only, T&Cs apply.`}
                                    emailSubject={`Join the race for ${prize}!`}
                                />
                            )}
                        </Styled.ShareWrapper>
                    )}
                    {bettingEnabled && (
                        <Styled.BetPromoWrapper>
                            <BetPromo promotionsConfig={promotionsConfig} />
                        </Styled.BetPromoWrapper>
                    )}
                    {!hasWinners && (
                        <Styled.PlayItv7Wrapper>
                            <PlayItv
                                titleText={`Loved playing the ${title} Sweepstake?`}
                                buttonText="PLAY ITV7"
                                TermsLink={Itv7TermsLink}
                                ButtonLink={ButtonLink}
                            />
                        </Styled.PlayItv7Wrapper>
                    )}
                    {!hasWinners && !hasEventStarted && (
                        <Styled.UpComingRoundsWrapper>
                            <UpcomingRounds />
                        </Styled.UpComingRoundsWrapper>
                    )}
                </Styled.ContentWrapper>
            </Layout>
        </>
    );
};

EnteredPage.defaultProps = {
    userName: null,
    didEntryWin: false,
    entry: {}
};

EnteredPage.propTypes = {
    showEntryAnimation: PropTypes.bool.isRequired,
    loggedIn: PropTypes.bool.isRequired,
    userName: PropTypes.string,
    excludeFromBetting: PropTypes.bool.isRequired,
    sweep: PropTypes.shape({
        prize: PropTypes.string,
        name: PropTypes.string,
        sweepId: PropTypes.string,
        event: PropTypes.shape({
            name: PropTypes.string,
            startDate: PropTypes.string
        })
    }).isRequired,
    hasEventStarted: PropTypes.bool.isRequired,
    hasWinners: PropTypes.bool.isRequired,
    winningSelections: PropTypes.arrayOf(PropTypes.shape(RunnerCard.propTypes))
        .isRequired,
    entry: PropTypes.shape(Entered.entry),
    didEntryWin: PropTypes.bool,
    TermsLink: PropTypes.func.isRequired,
    Itv7TermsLink: PropTypes.func.isRequired,
    ButtonLink: PropTypes.func.isRequired,
    displayCountdownTimer: PropTypes.bool.isRequired,
    bettingEnabled: PropTypes.bool.isRequired,
    // isGoldCup: PropTypes.bool.isRequired,
    promotionsConfig: PropTypes.shape({
        enabled: PropTypes.bool,
        href: PropTypes.string,
        img: PropTypes.shape({
            mobile: PropTypes.string,
            desktop: PropTypes.string
        })
    }).isRequired,
    shareEnabled: PropTypes.bool.isRequired
};

EnteredContainer.propTypes = {
    OnLoggedOut: PropTypes.node.isRequired
};

TermsLinkWrapper.propTypes = {
    children: PropTypes.node.isRequired
};

Itv7TermsLinkWrapper.propTypes = {
    children: PropTypes.node.isRequired
};

ButtonLinkWrapper.propTypes = {
    children: PropTypes.node.isRequired
};

export default EnteredContainer;
